import { lazy } from 'react';
import type { RouteObject } from 'react-router-dom';
import { RequireFeature } from '@/features/authentication';
import { LazyLoad } from '@/shared/components/LazyLoad';

const WorkflowConfigPage = lazy(() => import('./common/pages/WorkflowConfigPage'));

/**
 * `/workflow-config`.
 *
 * The path, the sidebar entry, `ROUTES.WORKFLOW_CONFIG` and the breadcrumb all
 * existed already and are untouched — this replaces the `PlaceholderPage` the
 * route rendered, and adds nothing beside it.
 *
 * <h2>The guard is the feature, and only the feature</h2>
 *
 * It was `requiredRoles={[ROLES.ADMIN]}` plus `requiredPermission="WORKFLOW_VIEW"`,
 * and both halves were wrong in the same way. The sidebar entry carries
 * `feature: 'Workflow Configuration'`, so an administrator who grants that feature
 * to Approver L1 puts Workflow Config in their menu — and the role check then
 * refused the click with "You do not have access to that page". `WORKFLOW_VIEW`
 * was worse: the platform retired the coded permissions in favour of the four
 * actions, so no session carries that code any more and the route was closed to
 * everybody, Admin included.
 *
 * `RequireFeature` asks the one question the sidebar, the page and the API all
 * ask — is `Workflow Configuration` readable for this user's role — so a menu
 * entry that appears is a URL that opens, and one that does not is a URL that
 * redirects. The same arrangement `masterRoutes` and `templateRoutes` use.
 *
 * Lazy, so the page's chunk is fetched only when the route is visited.
 */
export const workflowConfigRoute: RouteObject = {
  path: 'workflow-config',
  element: (
    <RequireFeature feature="Workflow Configuration">
      <LazyLoad>
        <WorkflowConfigPage />
      </LazyLoad>
    </RequireFeature>
  ),
};
